import React from 'react';

import { Tag } from 'antd';

import { ConvictionCoverageNote } from 'components';

import { SoloSessionItem } from './api';
import { SOLO_STATUS_COLORS, SOLO_STATUS_LABELS } from './helpers';

type SoloOutcomeItem = Pick<
  SoloSessionItem,
  'score_pct' | 'pass_bar' | 'conviction_coverage'
> & { status?: string };

/** 56.6, 73 — one decimal at most, never a percent: the score is points out
 *  of 100, and "56.6%" read as "56.6% of something". */
export const formatPoints = (value: number) => `${Math.round(value * 10) / 10}`;

const clamp = (value: number) => Math.max(0, Math.min(100, value));

/**
 * Where the score landed against the pass mark, on a 0–100 track.
 * Decorative: the sentence beside it carries both numbers.
 */
const ScoreMeter: React.FC<{ score: number; bar: number; passed: boolean }> = ({
  score,
  bar,
  passed,
}) => (
  <div className="cstudio__meter" aria-hidden="true">
    <div
      className={`cstudio__meter-fill cstudio__meter-fill--${
        passed ? 'pass' : 'short'
      }`}
      style={{ width: `${clamp(score)}%` }}
    />
    <div className="cstudio__meter-mark" style={{ left: `${clamp(bar)}%` }} />
  </div>
);

/**
 * A finished mini session's result, readable without knowing the product:
 * what happened (the tag), the score against what it needed, and how much of
 * the conviction surface the score stands on.
 */
const SoloOutcome: React.FC<{ item: SoloOutcomeItem }> = ({ item }) => {
  const status = item.status ?? '';
  // Nobody played the room: the tag says everything, and a baseline score
  // beside it only raises "how did they score anything?".
  const score = status === 'failed_no_participation' ? null : item.score_pct;
  const bar = item.pass_bar;
  return (
    <div className="cstudio__outcome">
      <div className="cstudio__outcome-line">
        <Tag color={SOLO_STATUS_COLORS[status]} style={{ marginInlineEnd: 0 }}>
          {SOLO_STATUS_LABELS[status] || status}
        </Tag>
        {score != null && (
          <span>
            Score <strong>{formatPoints(score)}</strong>
            {bar != null && (
              <span className="cstudio__outcome-needed">
                {' '}
                · needed {formatPoints(bar)} to pass
              </span>
            )}
          </span>
        )}
      </div>
      {score != null && bar != null && (
        <ScoreMeter score={score} bar={bar} passed={status === 'passed'} />
      )}
      {score != null && (
        <ConvictionCoverageNote coverage={item.conviction_coverage} />
      )}
    </div>
  );
};

export default SoloOutcome;
